import { createStore, useStore } from 'zustand';

import type { JwtClaims } from '@shared/types/auth';

interface AccessToken {
  token: string;
  expireAt: number;
}

interface TokenState {
  accessToken: AccessToken | null;
  setAccessToken: (token: string, accessTokenTTL?: number) => void;
  clearAccessToken: () => void;
  registerWsDisposer: (fn: () => void) => void;
}

let _wsDisposer: (() => void) | null = null;

function decodeExpireAt(token: string, accessTokenTTL?: number): number {
  if (accessTokenTTL !== undefined) {
    return Date.now() + accessTokenTTL - 2000;
  }
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? '')) as {
      exp?: number;
    };
    if (payload.exp) return payload.exp * 1000;
  } catch {
    // fallback: 1 hour
  }
  return Date.now() + 3600_000;
}

export const tokenStore = createStore<TokenState>()((set, get) => ({
  accessToken: null,

  setAccessToken: (token, accessTokenTTL) => {
    const prev = get().accessToken;
    const expireAt = decodeExpireAt(token, accessTokenTTL);
    const shouldUpdate = !prev || prev.token !== token || prev.expireAt - Date.now() < 10_000;
    if (shouldUpdate) {
      set({ accessToken: { token, expireAt } });
    }
  },

  clearAccessToken: () => {
    _wsDisposer?.();
    set({ accessToken: null });
  },

  registerWsDisposer: (fn) => {
    _wsDisposer = fn;
  },
}));

export function useTokenStore(): TokenState;
// eslint-disable-next-line no-redeclare
export function useTokenStore<T>(selector: (state: TokenState) => T): T;
// eslint-disable-next-line no-redeclare
export function useTokenStore<T>(selector?: (state: TokenState) => T) {
  return useStore(tokenStore, selector as (state: TokenState) => T);
}

export function isTokenValid(): boolean {
  const t = tokenStore.getState().accessToken;
  return !!t && t.token.length > 0 && t.expireAt > Date.now();
}

/**
 * Decodes JWT claims from the current access token.
 * Returns `null` when the token is missing, expired, or malformed.
 */
export function getTokenClaims(): JwtClaims | null {
  const t = tokenStore.getState().accessToken;
  if (!t || t.token.length === 0 || t.expireAt <= Date.now()) return null;
  try {
    return JSON.parse(atob(t.token.split('.')[1] ?? '')) as JwtClaims;
  } catch {
    return null;
  }
}

export function hasImpersonationClaim(token: string): boolean {
  try {
    return (JSON.parse(atob(token.split('.')[1] ?? '')) as JwtClaims).impersonating === true;
  } catch {
    return false;
  }
}

export function isImpersonating(): boolean {
  const t = tokenStore.getState().accessToken;
  return !!t && hasImpersonationClaim(t.token);
}

export function isTokenValidOrUndefined(): boolean {
  const t = tokenStore.getState().accessToken;
  if (!t) return true;
  return t.expireAt > Date.now();
}

export function shouldRefreshToken(): boolean {
  const t = tokenStore.getState().accessToken;
  if (!t) return false;
  return t.expireAt - Date.now() < 5 * 60 * 1000;
}

// ── Explicit-logout flag ──────────────────────────────────────────────────────
// Persisted (not just in-memory) because `logout()` navigates via a full page
// reload (`window.location.replace`), which wipes `tokenStore`. Every silent
// refresher (router bootstrap, AuthProvider mount, the 60s poll, the urql/ws
// exchanges) funnels through `requestRefresh()` — gating that one function on
// this flag stops all of them from re-minting an access token off the
// still-valid httpOnly refresh cookie after the user explicitly logged out,
// which previously left the app in a "data loads, identity doesn't" state.
const LOGGED_OUT_STORAGE_KEY = 'auth-logged-out';

export function markLoggedOut(): void {
  try {
    localStorage.setItem(LOGGED_OUT_STORAGE_KEY, '1');
  } catch {
    // storage unavailable (private mode, quota) — best-effort only
  }
}

export function clearLoggedOutFlag(): void {
  try {
    localStorage.removeItem(LOGGED_OUT_STORAGE_KEY);
  } catch {
    // storage unavailable — nothing to clear
  }
}

export function isLoggedOut(): boolean {
  try {
    return localStorage.getItem(LOGGED_OUT_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}
